import { useState, type FormEvent } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { categories } from "@/mocks/spotlight";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daihq6boh653ivfvodk0";

type ParsedResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) params.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const failed =
        !response.ok || (Boolean(code) && code !== "OK") || /spam/i.test(serverMsg);

      if (failed) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请检查内容后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Subscribe"
              title="订阅热点话题"
              desc="留下邮箱，第一时间收到新话题上线与热评风向变化提醒；也可以留言推荐你想讨论的话题。"
            />

            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="font-label text-sm font-medium text-foreground-900">联系邮箱</p>
                  <p className="mt-0.5 text-sm text-foreground-600">hello@heiliaokandian.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-send-plane-line"></i>
                </span>
                <div>
                  <p className="font-label text-sm font-medium text-foreground-900">话题投稿邮箱</p>
                  <p className="mt-0.5 text-sm text-foreground-600">contribute@heiliaokandian.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-shield-check-line"></i>
                </span>
                <div>
                  <p className="font-label text-sm font-medium text-foreground-900">内容反馈</p>
                  <p className="mt-0.5 text-sm text-foreground-600">
                    遇到不实或过激内容，欢迎邮件反馈，我们会按社区规范处理。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              id="topic-subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label htmlFor="subscribe-email" className="font-label text-sm text-foreground-800">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="subscribe-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  />
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="subscribe-topic" className="font-label text-sm text-foreground-800">
                    感兴趣的话题方向
                  </label>
                  <select
                    id="subscribe-topic"
                    name="topic"
                    className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  >
                    {categories
                      .filter((item) => item !== "全部")
                      .map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="subscribe-message" className="font-label text-sm text-foreground-800">
                    留言 / 话题推荐
                  </label>
                  <textarea
                    id="subscribe-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="说说你想讨论的话题，或你希望我们补充哪个角度（最多 500 字）"
                    className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                  ></textarea>
                </div>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp-field"
              />

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-notification-3-line"></i>
                  {status === "submitting" ? "提交中…" : "订阅并留言"}
                </button>
                <p className="text-xs text-foreground-500">
                  提交即表示你已阅读并同意我们仅将该信息用于话题提醒与联系。
                </p>
              </div>

              {status === "success" ? (
                <div className="mt-5 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-line"></i>
                  订阅成功，感谢你的关注，我们会尽快与你联系。
                </div>
              ) : null}

              {status === "error" ? (
                <div className="mt-5 flex items-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-900">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}